<script setup lang="ts">
    import { computed, ref, watch } from 'vue';
    import { __ } from '@/js/stores/expressions';
    import EchoButton from '@/js/components/EchoButton.vue';
    import FormGroup from '@/js/components/FormGroup.vue';
    import SelectInput from '@/js/components/SelectInput.vue';
    import EchoInfoBox from '@/js/components/EchoInfoBox.vue';
    import TextInput, { TextInputWidth } from '@/js/components/TextInput.vue';
    import RadioOption from '@/js/components/RadioOption.vue';
    import {
        allowsQueueAndSkillOverrides,
        isRetentionRuleImplemented,
        type RetentionDataType,
        type RetentionQueueOption,
        type RetentionSkillOption,
    } from '@/js/services/api/backOffice/retentionSettingsApi';

    export type RetentionBaseSettingEntry = {
        dataType: RetentionDataType;
        retentionDays: number;
        queueId: string | null;
        skillId: string | null;
    };

    type OverrideRow = {
        key: number;
        scope: 'queue' | 'skill';
        queueId: string;
        skillId: string;
        retentionDays: number | undefined;
    };

    const props = defineProps<{
        dataTypeOptions: RetentionDataType[];
        dataTypeLabel: (dataType: RetentionDataType) => string;
        dataTypeDescription: (dataType: RetentionDataType) => string;
        queueOptions: RetentionQueueOption[];
        skillOptions: RetentionSkillOption[];
        takenQueueIds: Set<string>;
        takenSkillIds: Set<string>;
        hasExistingBase: boolean;
        isPending: boolean;
    }>();

    const emit = defineEmits<{
        submit: [entries: RetentionBaseSettingEntry[]];
        cancel: [];
    }>();

    const dataType = defineModel<RetentionDataType>('dataType', { required: true });

    const baseRetentionDays = ref<number | undefined>(undefined);
    const overrideRows = ref<OverrideRow[]>([]);
    let nextRowKey = 0;

    const allowsOverrides = computed(() => allowsQueueAndSkillOverrides(dataType.value));
    watch(allowsOverrides, (allowed) => {
        if (!allowed) overrideRows.value = [];
    });

    const addOverrideRow = () => {
        overrideRows.value.push({
            key: nextRowKey++,
            scope: 'queue',
            queueId: '',
            skillId: '',
            retentionDays: undefined,
        });
    };

    const removeOverrideRow = (key: number) => {
        overrideRows.value = overrideRows.value.filter((row) => row.key !== key);
    };

    const isRowDuplicate = (row: OverrideRow) => {
        if (row.scope === 'queue' && row.queueId) {
            if (props.takenQueueIds.has(row.queueId)) return true;
            return overrideRows.value.some(
                (other) => other !== row && other.scope === 'queue' && other.queueId === row.queueId
            );
        }
        if (row.scope === 'skill' && row.skillId) {
            if (props.takenSkillIds.has(row.skillId)) return true;
            return overrideRows.value.some(
                (other) => other !== row && other.scope === 'skill' && other.skillId === row.skillId
            );
        }
        return false;
    };

    const isRowValid = (row: OverrideRow) =>
        !!row.retentionDays &&
        row.retentionDays > 0 &&
        (row.scope === 'queue' ? !!row.queueId : !!row.skillId) &&
        !isRowDuplicate(row);

    const canSubmit = computed(
        () =>
            !!baseRetentionDays.value &&
            baseRetentionDays.value > 0 &&
            !props.hasExistingBase &&
            overrideRows.value.every(isRowValid)
    );

    const onSubmit = () => {
        if (!canSubmit.value || baseRetentionDays.value == null) return;

        const entries: RetentionBaseSettingEntry[] = [
            {
                dataType: dataType.value,
                retentionDays: baseRetentionDays.value,
                queueId: null,
                skillId: null,
            },
            ...overrideRows.value.map((row) => ({
                dataType: dataType.value,
                retentionDays: row.retentionDays as number,
                queueId: row.scope === 'queue' ? row.queueId : null,
                skillId: row.scope === 'skill' ? row.skillId : null,
            })),
        ];
        emit('submit', entries);
    };
</script>
<template>
    <form class="flex flex-col gap-6" @submit.prevent="onSubmit">
        <FormGroup>
            <div class="flex flex-col gap-1">
                <label for="retention-base-data-type" class="text-overline">
                    {{ __('back-office-retention-data-type') }}
                </label>
                <SelectInput id="retention-base-data-type" v-model="dataType">
                    <option
                        v-for="dt in dataTypeOptions"
                        :key="dt"
                        :value="dt"
                        :disabled="!isRetentionRuleImplemented(dt)"
                    >
                        {{ dataTypeLabel(dt) }}
                        <template v-if="!isRetentionRuleImplemented(dt)">
                            ({{ __('back-office-retention-not-implemented') }})
                        </template>
                    </option>
                </SelectInput>
                <EchoInfoBox class="mt-1">
                    {{ dataTypeDescription(dataType) }}
                </EchoInfoBox>
            </div>

            <div class="flex flex-col gap-1">
                <label for="retention-base-days" class="text-overline">
                    {{ __('back-office-retention-base-days') }}
                </label>
                <TextInput
                    id="retention-base-days"
                    v-model.number="baseRetentionDays"
                    type="number"
                    min="1"
                    :width="TextInputWidth.Small"
                />
                <p v-if="hasExistingBase" class="text-body text-error">
                    {{ __('back-office-retention-duplicate-base') }}
                </p>
            </div>
        </FormGroup>

        <div v-if="allowsOverrides" class="flex flex-col gap-3">
            <div class="flex items-center justify-between">
                <span class="text-overline">
                    {{ __('back-office-retention-overrides-heading') }}
                </span>
                <EchoButton type="button" variant="secondary" @click="addOverrideRow">
                    {{ __('back-office-retention-add-override') }}
                </EchoButton>
            </div>

            <div
                v-for="row in overrideRows"
                :key="row.key"
                class="flex flex-col gap-3 rounded-md border border-gray-light p-3"
            >
                <div class="flex flex-col gap-2">
                    <span class="text-overline">{{ __('back-office-retention-scope') }}</span>
                    <div class="flex gap-6">
                        <RadioOption
                            :id="`retention-base-override-${row.key}-queue`"
                            v-model="row.scope"
                            :name="`retention-base-override-${row.key}-scope`"
                            value="queue"
                            :label="__('back-office-retention-scope-queue')"
                        />
                        <RadioOption
                            :id="`retention-base-override-${row.key}-skill`"
                            v-model="row.scope"
                            :name="`retention-base-override-${row.key}-scope`"
                            value="skill"
                            :label="__('back-office-retention-scope-skill')"
                        />
                    </div>
                </div>

                <div class="flex flex-wrap items-end gap-4">
                    <div v-if="row.scope === 'queue'" class="flex flex-col gap-1">
                        <label
                            :for="`retention-base-override-${row.key}-queue-select`"
                            class="text-overline"
                        >
                            {{ __('back-office-retention-queue') }}
                        </label>
                        <SelectInput
                            :id="`retention-base-override-${row.key}-queue-select`"
                            v-model="row.queueId"
                        >
                            <option value="" disabled>
                                {{ __('back-office-retention-select-queue') }}
                            </option>
                            <option v-for="q in queueOptions" :key="q.id" :value="q.id">
                                {{ q.name }}
                            </option>
                        </SelectInput>
                    </div>

                    <div v-else class="flex flex-col gap-1">
                        <label
                            :for="`retention-base-override-${row.key}-skill-select`"
                            class="text-overline"
                        >
                            {{ __('back-office-retention-skill') }}
                        </label>
                        <SelectInput
                            :id="`retention-base-override-${row.key}-skill-select`"
                            v-model="row.skillId"
                        >
                            <option value="" disabled>
                                {{ __('back-office-retention-select-skill') }}
                            </option>
                            <option v-for="s in skillOptions" :key="s.id" :value="s.id">
                                {{ s.name }}
                            </option>
                        </SelectInput>
                    </div>

                    <div class="flex flex-col gap-1">
                        <label
                            :for="`retention-base-override-${row.key}-days`"
                            class="text-overline"
                        >
                            {{ __('back-office-retention-days') }}
                        </label>
                        <TextInput
                            :id="`retention-base-override-${row.key}-days`"
                            v-model.number="row.retentionDays"
                            type="number"
                            min="1"
                            :width="TextInputWidth.Small"
                        />
                    </div>

                    <EchoButton
                        type="button"
                        variant="secondary"
                        @click="removeOverrideRow(row.key)"
                    >
                        {{ __('delete') }}
                    </EchoButton>
                </div>

                <p v-if="isRowDuplicate(row)" class="text-body text-error">
                    {{ __('back-office-retention-duplicate-scope') }}
                </p>
            </div>
        </div>

        <div class="flex justify-end gap-3">
            <EchoButton type="submit" variant="primary" :disabled="!canSubmit" :loading="isPending">
                {{ __('save') }}
            </EchoButton>
            <EchoButton type="button" variant="secondary" @click="emit('cancel')">
                {{ __('cancel') }}
            </EchoButton>
        </div>
    </form>
</template>
